import { Button, List, Message, Modal } from "@arco-design/web-react";
import { Member } from "@sendbird/chat/lib/__definition";
import axios from "axios";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { endpoints } from "../../../../api/endpoints";
import { SYSTEM_MODERATOR_USER_ID } from "../../constants";

import "./ViewGroupMembersDialog.css";

type Props = {
  visible: boolean;
  channelUrl: string;
  canManageChat: boolean;
  onClose: () => void;
  onUpdated: () => void;
};

type MemberRow = {
  member: Member;
  displayName: string;
  isPlatformAdmin: boolean;
  isMentor: boolean;
};

function isAdminMember(row: MemberRow): boolean {
  return row.member.role === "operator" || row.isPlatformAdmin;
}

function sortMemberRows(rows: MemberRow[]): MemberRow[] {
  const members = rows.filter((row) => !isAdminMember(row));
  const admins = rows.filter((row) => isAdminMember(row));

  const byName = (left: MemberRow, right: MemberRow) =>
    left.displayName.localeCompare(right.displayName);

  return [...members.sort(byName), ...admins.sort(byName)];
}

async function resolveMemberRows(members: Member[]): Promise<MemberRow[]> {
  const eligibleMembers = members.filter(
    (member) => member.userId !== SYSTEM_MODERATOR_USER_ID
  );

  const profiles = await Promise.allSettled(
    eligibleMembers.map((member) =>
      axios.get(`${endpoints.getPublicProfile}${member.userId}`)
    )
  );

  const rows = eligibleMembers.map((member, index) => {
    const profileResult = profiles[index];
    let displayName = member.nickname?.trim() || `User ${member.userId}`;
    let isPlatformAdmin = false;
    let isMentor = false;

    if (profileResult.status === "fulfilled") {
      const profile = profileResult.value.data as {
        name?: string;
        last_name?: string;
        isAdmin?: boolean;
        isMentor?: boolean;
      };
      isPlatformAdmin = Boolean(profile.isAdmin);
      isMentor = Boolean(profile.isMentor);
      const fullName = [profile.name, profile.last_name].filter(Boolean).join(" ").trim();
      if (fullName) {
        displayName = fullName;
      }
    }

    return { member, displayName, isPlatformAdmin, isMentor };
  });

  return sortMemberRows(rows);
}

function getAdminNote(row: MemberRow, t: (key: string) => string): string | null {
  if (row.member.role === "operator") {
    return t("messages.group_admin_subtitle");
  }
  if (row.isPlatformAdmin) {
    return t("messages.platform_admin_subtitle");
  }
  return null;
}

function getRoleLabel(
  row: MemberRow,
  isSelf: boolean,
  t: (key: string) => string
): string {
  if (isSelf) {
    return t("messages.you_label");
  }
  if (row.isMentor) {
    return t("messages.mentor_badge");
  }
  return t("messages.mentee_badge");
}

export function ViewGroupMembersDialog({
  visible,
  channelUrl,
  canManageChat,
  onClose,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const {
    stores: {
      sdkStore: { sdk },
      userStore,
    },
  } = useSendbirdStateContext();
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const loadMembers = useCallback(async () => {
    if (!channelUrl) {
      return;
    }

    const channel = await sdk.groupChannel.getChannel(channelUrl);
    const rows = await resolveMemberRows(channel.members ?? []);
    setMemberRows(rows);
  }, [channelUrl, sdk.groupChannel]);

  useEffect(() => {
    const load = async () => {
      if (!visible || !channelUrl) {
        return;
      }

      setIsLoading(true);
      try {
        await loadMembers();
      } catch {
        setMemberRows([]);
      } finally {
        setIsLoading(false);
      }
    };

    load().catch(console.error);
  }, [visible, channelUrl, loadMembers]);

  const handleRemoveMember = async (row: MemberRow) => {
    const { member } = row;
    if (member.userId === userStore.user?.userId) {
      return;
    }

    setRemovingUserId(member.userId);
    try {
      const channel = await sdk.groupChannel.getChannel(channelUrl);
      await channel.banUserWithUserId(member.userId);
      await loadMembers();
      onUpdated();
      Message.success({
        content: t("messages.remove_from_group_success"),
        closable: true,
        duration: 3000,
      });
    } catch {
      Message.error({
        content: t("messages.remove_from_group_error"),
        closable: true,
        duration: 4000,
      });
    } finally {
      setRemovingUserId(null);
    }
  };

  const currentUserId = userStore.user?.userId;

  return (
    <Modal
      title={t("messages.view_members_modal_title")}
      visible={visible}
      onCancel={onClose}
      className="view-group-members-dialog"
      footer={null}
    >
      <div className="view-group-members-dialog__content">
        <List
          className="view-group-members-dialog__list list-demo-actions"
          loading={isLoading}
          size="small"
          bordered={false}
          dataSource={memberRows}
          render={(row) => {
            const isSelf = row.member.userId === currentUserId;
            const isAdmin = isAdminMember(row);
            const adminNote = getAdminNote(row, t);
            const canRemove =
              canManageChat && !isSelf && row.member.userId !== SYSTEM_MODERATOR_USER_ID;
            const showRolePill = !canRemove && (!isAdmin || isSelf);

            return (
              <List.Item key={row.member.userId}>
                <div className="view-group-members-dialog__row">
                  <span className="view-group-members-dialog__member-name">
                    {row.displayName}
                  </span>
                  {adminNote ? (
                    <span className="view-group-members-dialog__admin-note">
                      {adminNote}
                    </span>
                  ) : (
                    <span className="view-group-members-dialog__admin-note view-group-members-dialog__admin-note--empty" />
                  )}
                  <div className="view-group-members-dialog__action-slot">
                    {canRemove ? (
                      <Button
                        className="view-group-members-dialog__remove-button"
                        size="mini"
                        shape="round"
                        loading={removingUserId === row.member.userId}
                        onClick={() => handleRemoveMember(row)}
                      >
                        {t("messages.remove_from_group_button")}
                      </Button>
                    ) : showRolePill ? (
                      <span className="view-group-members-dialog__pill">
                        {getRoleLabel(row, isSelf, t)}
                      </span>
                    ) : (
                      <span className="view-group-members-dialog__action-spacer" />
                    )}
                  </div>
                </div>
              </List.Item>
            );
          }}
          noDataElement={
            <div className="view-group-members-dialog__empty">
              {t("messages.view_members_empty")}
            </div>
          }
        />
      </div>
    </Modal>
  );
}
